import store from '../../redux/store'
import { showMessageModal } from '../../redux/actions'
import { createCustomerBackend } from '../backend'
import { getFirebaseAuth } from './firebaseInit'
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth'
import { isStringNotNull } from '../../utils'
import { transferCustomerData } from '../users'
import { logHighError, logLowLog, logMediumError } from '../../utils/logger'

//----------------------------------------
// FIREBASE AUTHENTICATION SERVICES
//----------------------------------------
export const getFirebaseCustomerId = () => {
  const auth = getFirebaseAuth();
  const { currentUser } = auth || {};
  return currentUser ? currentUser.uid : null;
};

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

let cachedToken = null;
let expiresAt = 0;
const SKEW_MS = 60 * 1000;

const updateRefreshedToken = async (user) => {
  const tokenResult = await user.getIdTokenResult(true)
  cachedToken = tokenResult.token
  expiresAt = new Date(tokenResult.expirationTime).getTime()
}

const refreshAutomaticallyToken = async () => {
  try {
    const auth = getFirebaseAuth();
    const user = auth?.currentUser;
    if (user) {
      await updateRefreshedToken(user)
    }
  } catch {
    console.warn('Could not refresh automatically token!')
  }
  await wait((60 * 60 * 1000) - SKEW_MS);
  return refreshAutomaticallyToken();
};

refreshAutomaticallyToken()

export const getIdToken = async (forceRefresh = false) => {
  try {
    const auth = getFirebaseAuth();
    const user = auth?.currentUser;
    if (!user) {
      return null
    }
    const now = Date.now();
    if (!forceRefresh && cachedToken && now < (expiresAt - SKEW_MS)) {
      return cachedToken;
    }
    await updateRefreshedToken(user)
    return cachedToken;
  } catch {
    return null
  }
}

export const signOut = async () => {
  const { user: stateUser } = store.getState().userReducer;
  const auth = getFirebaseAuth();
  await auth.signOut();
  cachedToken = null
  expiresAt = 0;
  const anonymousUser = await anonymousUserConnexion();
  if (
    isStringNotNull(stateUser?.customerId) &&
    isStringNotNull(anonymousUser?.user?.uid) &&
    stateUser?.customerId !== anonymousUser?.user?.uid
  ) {
    await transferCustomerData(stateUser?.customerId, anonymousUser.user.uid);
  }
  await getIdToken(true)
};

export const signInUser = async (email, password) => {
  const auth = getFirebaseAuth();
  try {
    const response = await signInWithEmailAndPassword(auth, email, password);
    await getIdToken(true)
    return response.user.uid;
  } catch (error) {
    const errorMessage = String(error);
    if (
      !errorMessage.includes('already') &&
      !errorMessage.includes('6 characters') &&
      !errorMessage.includes('user does not have a password') &&
      !errorMessage.includes('There is no user record')
    ) {
      logMediumError(`Error login ${errorMessage}`);
    }
    const errorCode = error.code;
    throw errorCode;
  }
};

export const deleteUser = async () => {
  // TODO delete user also in backend. But this should be a
  // backend transaction to avoid coherence issues
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (user) {
    try {
      logLowLog(`Delete user ${user.uid}`);
      await user.delete();
    } catch (error) {
      logMediumError(`Could not delete user ${error}`);
      throw error;
    }
  }
};

export const FIREBASE_USER_FLAG = 'pendingUser';

export const generatePendingUserFlag = (customerId) =>
  `${FIREBASE_USER_FLAG}_${customerId}`;

export const signUpUser = async (
  email,
  password,
  lastName,
  firstName,
  numero,
  phone,
  newsletter
) => {
  const auth = getFirebaseAuth();
  try {
    const response = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = response.user;
    const customerId = user.uid;
    await updateProfile(user, {
      displayName: generatePendingUserFlag(customerId),
    });
    const profile = {
      firstName,
      lastName,
      email,
      numero,
      phone,
      newsletter,
      id: customerId,
    };
    await createCustomerBackend(profile);
    return customerId;
  } catch (error) {
    const errorMessage = String(error);
    logMediumError(`Error sign up ${errorMessage}`);
    const errorCode = error.code;
    throw errorCode;
  }
};

export const forgetPasswordUser = async (email) => {
  const auth = getFirebaseAuth();
  try {
    await sendPasswordResetEmail(auth, email);
    store.dispatch(
      showMessageModal(
        'Un email vous a été envoyé, cliquez sur le lien dans celui-ci pour réinitialiser votre mot de passe'
      )
    );
  } catch (error) {
    const errorCode = error.code;
    throw errorCode;
  }
};


export const anonymousUserConnexion = async () => {
  const auth = getFirebaseAuth();
  try {
    const response = await signInAnonymously(auth);
    return response.user.uid;
  } catch (error) {
    logHighError(`Error sign in anonymous user ${error}`);
    throw error;
  }
};
